import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue';

import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { useMounted } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useAppStore } from '@/stores/pinia/useAppStore';

// Will be set to true after first navigation to ensure we stop using the persisted ssrTodaysDate
const didUpdateServerDate = ref(false);

/**
 * Composable to persist today's date across SSR and client side.
 *
 * - During SSR, it sets the date using the server's timezone and stores it in `ssrTodaysDate`.
 * - On the client, it updates to the correct date based on the timezone.
 * - Prevents hydration mismatches by using the precomputed SSR date before mounting.
 *
 * @returns Today's date shifted by the timezone.
 */
export function usePersistedTodaysDate() {
	const appStore = useAppStore();
	const { setSSRTodaysDate } = appStore;
	const { ssrTodaysDate } = storeToRefs(appStore);
	const { localeObject } = storeToRefs(useLanguageStore());
	const isMounted = useMounted();
	const { activeRoute } = storeToRefs(useRoutingStore());

	onServerPrefetch(() => {
		setSSRTodaysDate(shiftDateWithTimezone(localeObject.value.timezone));
	});

	onMounted(() => {
		if (didUpdateServerDate.value) return;

		const unwatch = watch(activeRoute, () => {
			didUpdateServerDate.value = true;
			unwatch();
		});
	});

	const todayDate = computed(() => {
		if (
			ssrTodaysDate.value &&
			// first if the component not yet mounted we keep returning ssrTodaysDate.
			!isMounted.value &&
			// if the user did navigate to another page we return the clients date
			!didUpdateServerDate.value
		) {
			return new Date(ssrTodaysDate.value);
		}

		return shiftDateWithTimezone(localeObject.value.timezone);
	});

	return {
		todayDate,
	};
}
